import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import * as challengesApi from '../utils/challengesApi';
import SportGrid from './SportGrid';
import ChallengeCard from './ChallengeCard';
import ChallengeModal from './ChallengeModal';
import TopPlayersPanel from './TopPlayersPanel';
import UserStatsPanel from './UserStatsPanel';
import '../styles/ChallengesPage-Modern.css';

export default function ChallengesPage() {
  const { currentUser } = useAuth();
  const toast = useContext(ToastContext);

  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin' || currentUser?.isAdmin === true;

  // Modal state for editing challenges
  const [modal, setModal] = useState(null);

  const [selectedSport, setSelectedSport] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  // track challenge currently animating/selected for proof submission
  const [animatingId, setAnimatingId] = useState(null);
  // track challenge cancellations with expiry (endDate) so user cannot rejoin
  // shape: { [challengeId]: expiresAtISO }
  const [cancelledMap, setCancelledMap] = useState(() => {
    if (!currentUser) return {};
    try {
      const stored = localStorage.getItem(`cancelled_challenges_${currentUser.uid}`);
      const parsed = stored ? JSON.parse(stored) : {};
      const now = Date.now();
      const cleaned = {};
      Object.entries(parsed).forEach(([k, v]) => {
        if (!v) return;
        const t = new Date(v).getTime();
        if (!isNaN(t) && t > now) cleaned[k] = v;
      });
      return cleaned;
    } catch (err) {
      return {};
    }
  });
  const [lastCancelledId, setLastCancelledId] = useState(null);

  // Load challenges when sport is selected
  useEffect(() => {
    if (!selectedSport) return;
    const loadChallenges = async () => {
      try {
        setLoading(true);
        const data = await challengesApi.filterChallenges({ sport: selectedSport });
        console.log('📊 Challenges loaded:', data, 'Sport:', selectedSport);
        // data is { success, count, challenges }
        const challenges = Array.isArray(data) ? data : (data?.challenges || []);
        console.log('📌 Filtered challenges:', challenges);
        // Debug: show userStatus for each challenge
        challenges.forEach(c => {
          console.log(`Challenge: ${c.title}, userProgress:`, c.userProgress, 'userStatus:', c.userProgress?.userStatus);
        });
        // Ensure we only keep `userProgress` when it belongs to the current user.
        // Some backends may return progress objects for other users; that would incorrectly
        // mark challenges as 'en cours' and prevent participation. Remove foreign progress.
        const normalized = challenges.map(c => {
          const progress = c.userProgress;
          if (!progress || !currentUser) return c;
          const ownerId = progress.userId || progress.uid || progress.user || progress.user_id || progress.userUid;
          // if ownerId exists and doesn't match the current user, drop the progress
          if (ownerId && currentUser && ownerId !== currentUser.uid && ownerId !== currentUser.id && ownerId !== currentUser.email) {
            return { ...c, userProgress: null };
          }
          return c;
        });

        setChallenges(normalized);
      } catch (err) {
        console.error('❌ Error loading challenges:', err);
        setChallenges([]);
      } finally {
        setLoading(false);
      }
    };
    loadChallenges();
  }, [selectedSport, currentUser]);

  // Persist cancelled challenges map to localStorage (only non-expired)
  useEffect(() => {
    if (!currentUser) return;
    try {
      const now = Date.now();
      const cleaned = {};
      Object.entries(cancelledMap).forEach(([k, v]) => {
        const t = new Date(v).getTime();
        if (!isNaN(t) && t > now) cleaned[k] = v;
      });
      localStorage.setItem(`cancelled_challenges_${currentUser.uid}`, JSON.stringify(cleaned));
      // keep state in sync with cleaned
      if (Object.keys(cleaned).length !== Object.keys(cancelledMap).length) {
        setCancelledMap(cleaned);
      }
    } catch (err) {
      console.error('Error persisting cancelled challenges', err);
    }
  }, [cancelledMap, currentUser]);

  // Clean expired cancellations on mount / when user changes
  useEffect(() => {
    if (!currentUser) return;
    try {
      const stored = localStorage.getItem(`cancelled_challenges_${currentUser.uid}`);
      if (!stored) return;
      const parsed = JSON.parse(stored || '{}');
      const now = Date.now();
      const cleaned = {};
      Object.entries(parsed).forEach(([k, v]) => {
        const t = new Date(v).getTime();
        if (!isNaN(t) && t > now) cleaned[k] = v;
      });
      if (Object.keys(cleaned).length !== Object.keys(parsed).length) {
        localStorage.setItem(`cancelled_challenges_${currentUser.uid}`, JSON.stringify(cleaned));
      }
      setCancelledMap(cleaned);
    } catch (err) {
      console.error('Error cleaning cancelled challenges on load', err);
    }
  }, [currentUser]);

  // when a challenge is cancelled, scroll its card into view and show toast
  useEffect(() => {
    if (!lastCancelledId) return;
    const el = document.getElementById(`challenge-card-${lastCancelledId}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (toast?.info) toast.info('Vous avez annulé ce défi. Vous ne pouvez pas y participer.');
    // clear after a short delay
    const t = setTimeout(() => setLastCancelledId(null), 1500);
    return () => clearTimeout(t);
  }, [lastCancelledId, toast]);

  const handleParticipate = async (challenge) => {
    // Prevent admin users from participating
    if (isAdmin) {
      if (toast?.info) toast.info('Les administrateurs ne peuvent pas participer aux défis');
      return false;
    }
    
    // Check local cancelled map - if user cancelled this challenge, they cannot rejoin
    const cancelledUntil = cancelledMap[challenge._id];
    const isLocallyCancelled = !!(cancelledUntil && new Date(cancelledUntil).getTime() > Date.now());
    if (isLocallyCancelled) {
      if (toast?.info) toast.info('Vous avez annulé ce défi. Vous ne pouvez pas y participer.');
      return false;
    }
    
    // Check if already actively participating (has active progress on server)
    if (challenge.userProgress && (challenge.userProgress.userStatus === 'active' || challenge.userProgress.userStatus === 'pending_review')) {
      if (toast?.info) toast.info('Vous participez déjà à ce défi');
      return false;
    }
    
    if (challenge._isPlaceholder || (typeof challenge._id==='string' && challenge._id.startsWith('ph-'))) {
      if (toast?.info) toast.info('Ce défi est un exemple : participation désactivée.');
      return false;
    }

    try {
      console.log(`🎯 Participating in challenge:`, challenge._id, challenge.title);
      setAnimatingId(challenge._id);
      const res = await challengesApi.participateChallenge(challenge._id);
      console.log(`📡 Participate response:`, res);
      
      const progress = res?.progress;
      if (!progress) {
        console.warn('⚠️ No progress in response:', res);
        throw new Error('Invalid response: no progress data');
      }
      
      console.log(`✅ Progress received:`, progress);
      
      // compute new endDate so countdown starts now
      let updatedChallenge = { ...challenge, userProgress: progress };
      try {
        const origStart = new Date(challenge.startDate).getTime();
        const origEnd = new Date(challenge.endDate).getTime();
        if (!isNaN(origStart) && !isNaN(origEnd) && origEnd > origStart) {
          const duration = origEnd - origStart;
          updatedChallenge.endDate = new Date(Date.now() + duration).toISOString();
        }
      } catch {};
      // update the challenge in list so userProgress and new endDate are available
      setChallenges(prev => prev.map(c => c._id === challenge._id ? updatedChallenge : c));
      if (toast?.success) toast.success('Participation enregistrée');
      console.log(`🎉 Challenge participation successful`);
      return true;
    } catch (e) {
      console.error('❌ Participate error:', e);
      if (toast?.error) toast.error('Erreur participation: ' + (e.message || e.toString()));
      setAnimatingId(null);
      return false;
    }
  };


  // If no sport selected, show carousel
  if (!selectedSport) {
    return (
      <div className="challenges-page challenges-empty">
        <div className="challenges-container">
          <SportGrid selected={selectedSport} onSelect={setSelectedSport} showCarousel={true} />
        </div>
      </div>
    );
  }

  const handleCancel = async (challenge) => {
    // Show warning confirmation before cancelling
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre participation à ce défi ? Cette action est irréversible.')) {
      return;
    }
    
    // Call the API to cancel the challenge on the server
    try {
      if (challenge.userProgress && challenge.userProgress._id) {
        await challengesApi.cancelChallenge(challenge.userProgress._id);
      }
    } catch (err) {
      console.error('Error cancelling challenge on server:', err);
      // Continue with local cancellation even if server call fails
    }
    
    // Add to cancelled map with a permanent expiry (10 years from now)
    // This ensures once a user cancels, they cannot re-participate in that challenge
    const expiresAt = new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString();
    setCancelledMap(prev => ({ ...prev, [challenge._id]: expiresAt }));
    // mark challenge as cancelled in list (removes any userProgress)
    setChallenges(prev => prev.map(c => c._id === challenge._id ? { ...c, userProgress: null } : c));
    // expose cancelled id so we can scroll to it and show a toast
    setLastCancelledId(challenge._id);
    setAnimatingId(null);
  };

  // Admin functions for edit and delete
  const handleEditChallenge = (challenge) => {
    // Open the edit modal with the challenge data
    setModal({ type: 'edit', challenge });
  };

  const handleDeleteChallenge = async (challenge) => {
    if (!challenge._id || challenge._id.startsWith('ph-')) {
      if (toast?.error) toast.error('Ce défi ne peut pas être supprimé');
      return;
    }
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce défi ?')) {
      try {
        await challengesApi.deleteChallenge(challenge._id);
        // Remove from list
        setChallenges(prev => prev.filter(c => c._id !== challenge._id));
        if (toast?.success) toast.success('Défi supprimé');
      } catch (e) {
        console.error('Delete error:', e);
        if (toast?.error) toast.error('Erreur lors de la suppression');
      }
    }
  };

  // Handle when challenge is saved from modal
  const handleModalSaved = (savedChallenge) => {
    setModal(null);
    // Update the challenge in the list
    setChallenges(prev => prev.map(c => 
      c._id === savedChallenge._id ? { ...c, ...savedChallenge } : c
    ));
    if (toast?.success) toast.success('Défi mis à jour');
  };


  // If sport selected, show 3 difficulty cards + sidebars
  return (
    <div className="challenges-page">
      <div className="challenges-container">
        <div className="back-button" onClick={() => setSelectedSport(null)}>
          ← Retour aux sports
        </div>

        <div className="challenges-main">
          <div className="challenges-column">
            <h2 className="sport-title">Défis {selectedSport}</h2>
            {loading ? (
              <div className="loading">Chargement...</div>
            ) : (
              <div className={`levels-grid${animatingId ? ' single' : ''}`}>
                {challenges.length > 0 ? (
                  // Display ALL challenges regardless of user status - don't filter by status
                  (() => {
                    const displayedChallenges = challenges.slice(0, 3);
                    
                    return displayedChallenges.map((challenge) => {
                      const cancelledUntil = cancelledMap[challenge._id];
                      const isPermanentlyCancelled = !!(cancelledUntil && new Date(cancelledUntil).getTime() > Date.now()) || challenge.cancelled;
                    return (
                        <ChallengeCard
                          key={challenge._id}
                          challenge={challenge}
                          isPermanentlyCancelled={isPermanentlyCancelled}
                          onClick={() => {
                            setAnimatingId(challenge._id);
                            setTimeout(() => {
                              setAnimatingId(null);
                            }, 350);
                          }}
                          onParticipate={handleParticipate}
                          onCancelParticipation={handleCancel}
                          isAdmin={isAdmin}
                          onEdit={handleEditChallenge}
                          onDelete={handleDeleteChallenge}
                        />
                      );
                    });
                  })()
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                    <p>📭 Aucun défi disponible pour {selectedSport}</p>
                    <p style={{ fontSize: '0.9rem' }}>Revenez bientôt !</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="challenges-sidebar">
            <TopPlayersPanel sport={selectedSport} />
            <UserStatsPanel />
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {modal && modal.type === 'edit' && (
        <ChallengeModal
          isOpen={true}
          initialData={modal.challenge}
          onClose={() => setModal(null)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}

